import { Program, type GL } from "./gl";

export enum Shape {
  Infantry = 0,
  Tank = 1,
  Truck = 2,
  Mortar = 3,
  Unit = 4,
  AntiTank = 5,
  /** Robot infantry: a hexagon, angular where a soldier is round. */
  Robot = 6,
  /** A fixed emplacement (a guard post): a hard square that never moves. */
  Post = 7,
  /** A storm creature (v31.13): a jagged star, no side's shape. */
  Storm = 8,
  Depot = 10,
  Producer = 11,
  Train = 12,
  Projectile = 13,
  Burst = 14,
  Death = 15,
  TargetRing = 16,
  Selection = 17,
  Mine = 18,
  Plant = 19,
  /** Structures by kind (a depot's `kind`), on the structure plate. */
  Home = 20,
  Station = 21,
  MineWorks = 22,
  PlantWorks = 23,
  /** A titan foundry: a titan's hex footprint, a core within. */
  Foundry = 24,
}

/** A unit icon's parts, from its design's modules (the sprite's spare byte):
 *  the body is the chassis (`Shape`), and each module adds a mark, so what a
 *  unit carries shows on it. */
export const Part = {
  /** A weapon that goes through armour, carried by foot: a bar across. */
  ANTI_ARMOUR: 1,
  /** A vehicle's gun: a barrel forward. */
  GUN: 2,
  /** Indirect fire: a chevron. */
  MORTAR: 4,
  /** Armour: a second outline inside, a plate. */
  ARMOUR: 8,
  /** Cargo: a box (filled when loaded). */
  CARGO: 16,
  /** No weapon: hollow. */
  UNARMED: 32,
  /** Builds (engineers): a wrench. */
  BUILDER: 64,
  /** Prospects: a survey tripod. */
  SURVEY: 128,
} as const;

export const SpriteFlag = {
  DRY: 1,
  SUPPRESSED: 2,
  SELECTED: 4,
  DUG_IN: 8,
  CONVOY: 16,
  RETURNING: 32,
  LOADED: 64,
  /** Depots: a field depot. Units: an engine or gun knocked out. */
  FIELD: 128,
  KNOCKED_OUT: 128,
} as const;

const FLOATS = 5; // x, y, radius, shape, angle
const BYTES = 8; // r, g, b, a, hp, flags, aux, spare

const VERTEX = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aCorner;
layout(location = 1) in vec4 aPos;     // x, y, radius, shape
layout(location = 2) in float aAngle;
layout(location = 3) in vec4 aColor;
layout(location = 4) in vec4 aExtra;   // hp, flags, aux, parts (0..255)
uniform vec2 uCenter;
uniform float uZoom;
uniform vec2 uViewport;
uniform float uMinPx;
out vec2 vLocal;
out vec4 vColor;
flat out int vShape;
flat out int vFlags;
flat out int vParts;
out float vHp;
out float vAux;
out float vPx;
void main() {
  float radius = max(aPos.z, uMinPx / uZoom);
  float c = cos(aAngle), s = sin(aAngle);
  vec2 corner = aCorner * 1.25;
  vec2 offset = vec2(corner.x * c - corner.y * s, corner.x * s + corner.y * c) * radius;
  vec2 screen = (aPos.xy + offset - uCenter) * uZoom;
  gl_Position = vec4(screen.x / uViewport.x * 2.0, -screen.y / uViewport.y * 2.0, 0.0, 1.0);
  vLocal = corner;
  vColor = aColor;
  vShape = int(aPos.w + 0.5);
  vFlags = int(aExtra.y * 255.0 + 0.5);
  vParts = int(aExtra.w * 255.0 + 0.5);
  vHp = aExtra.x;
  vAux = aExtra.z;
  vPx = 1.0 / (radius * uZoom);
}`;

const FRAGMENT = `#version 300 es
precision highp float;
in vec2 vLocal;
in vec4 vColor;
flat in int vShape;
flat in int vFlags;
flat in int vParts;
in float vHp;
in float vAux;
in float vPx;
uniform float uTime;
uniform sampler2D uPalette;  // side colours: texel n is side n (any number of sides)
out vec4 outColor;

float box(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}
float seg(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  return length(pa - ba * clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0));
}
float hexDist(vec2 p) {
  p = abs(p);
  return max(p.x, 0.5 * p.x + 0.8660254 * p.y);
}
float tri(vec2 p, float r) {
  const float k = 1.7320508;
  p.y = -p.y + r * 0.25;
  p.x = abs(p.x) - r;
  p.y = p.y + r / k;
  if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
  p.x -= clamp(p.x, -2.0 * r, 0.0);
  return -length(p) * sign(p.y);
}

void main() {
  vec2 p = vLocal;
  float aa = vPx * 1.5;
  vec4 col = vColor;
  float d;
  bool unit = vShape <= 8;
  if (vShape == 0 || vShape == 5) d = length(p) - 0.8;
  else if (vShape == 6) d = max(abs(p.x) * 0.866 + abs(p.y) * 0.5, abs(p.y)) - 0.74;
  else if (vShape == 7) d = box(p, vec2(0.74, 0.74), 0.0);
  else if (vShape == 8) {
    // A storm creature: an eight-pointed jagged star.
    float a = atan(p.y, p.x);
    float r = 0.52 + 0.3 * pow(abs(cos(a * 4.0)), 3.0);
    d = length(p) - r;
  }
  else if (vShape == 1) d = box(p, vec2(0.8, 0.68), 0.12);
  else if (vShape == 2) d = box(p, vec2(0.92, 0.48), 0.2);
  else if (vShape == 3) d = min(length(p - vec2(0.2, 0.0)) - 0.6, box(p + vec2(0.55, 0.0), vec2(0.4, 0.08), 0.02));
  else if (vShape == 4) d = abs(p.x) + abs(p.y) - 0.9;
  else if (vShape == 10 || vShape == 11 || (vShape >= 20 && vShape <= 24)) {
    // Structures: a dark plate with a border in the owner's colour, so they
    // read on any ground, and a glyph for what they are.
    float plate = box(p, vec2(0.86), 0.2);
    float a = 1.0 - smoothstep(-aa, aa, plate);
    float border = 1.0 - smoothstep(-aa, aa, abs(plate + 0.06) - 0.06);
    vec3 c = vec3(0.07, 0.08, 0.1);
    vec3 team = vColor.rgb;
    bool idle = (vFlags & 1) != 0;
    if (vShape == 11 || vShape == 21 || vShape == 22 || vShape == 23 || vShape == 24) {
      float g;
      if (vShape == 24) {
        // Foundry: a titan's footprint, a hexagon ring, round a solid core.
        float hx = max(abs(p.x) * 0.866 + abs(p.y) * 0.5, abs(p.y));
        g = min(abs(hx - 0.5) - 0.08, hx - 0.16);
      } else if (vShape == 11) {
        // Barracks: a soldier's chevrons.
        float c1 = min(seg(p, vec2(-0.46, -0.02), vec2(0.0, -0.34)), seg(p, vec2(0.46, -0.02), vec2(0.0, -0.34)));
        float c2 = min(seg(p, vec2(-0.46, 0.36), vec2(0.0, 0.04)), seg(p, vec2(0.46, 0.36), vec2(0.0, 0.04)));
        g = min(c1, c2) - 0.1;
      } else if (vShape == 21) {
        // Station: two rails on their sleepers.
        float rails = box(vec2(p.x, abs(p.y) - 0.2), vec2(0.6, 0.05), 0.01);
        float sleepers = box(vec2(abs(abs(p.x) - 0.2) - 0.2, p.y), vec2(0.06, 0.36), 0.01);
        g = min(rails, sleepers);
      } else if (vShape == 22) {
        // Mine: a headframe, its wheel on top.
        float legs = min(seg(p, vec2(-0.42, 0.5), vec2(0.0, -0.28)), seg(p, vec2(0.42, 0.5), vec2(0.0, -0.28))) - 0.07;
        float base = seg(p, vec2(-0.56, 0.52), vec2(0.56, 0.52)) - 0.06;
        float wheel = abs(length(p - vec2(0.0, -0.3)) - 0.2) - 0.07;
        g = min(min(legs, base), wheel);
      } else {
        // Plant: a works, sawtooth roof and a chimney.
        vec2 q = p - vec2(0.0, 0.08);
        float body = box(q - vec2(0.0, 0.18), vec2(0.56, 0.26), 0.02);
        float tooth = q.y + 0.05 - 0.28 * (1.0 - fract((q.x + 0.56) * 1.6));
        float roof = max(max(-tooth, -q.y - 0.12), max(abs(q.x) - 0.56, q.y - 0.12));
        float chimney = box(q - vec2(0.36, -0.36), vec2(0.08, 0.22), 0.01);
        g = min(min(body, roof), chimney);
      }
      // Mines and plants fill from the bottom as they're built (the rest
      // dim); idle (waiting for steel, or nothing made): greyed, a red bar
      // beneath.
      float built = (vShape == 22 || vShape == 23) ? step(mix(0.62, -0.62, vHp), p.y) : 1.0;
      vec3 ink = idle ? vec3(0.35) : team;
      c = mix(c, mix(ink * 0.35, ink, built), 1.0 - smoothstep(-aa, aa, g));
      if (idle) c = mix(c, vec3(0.9, 0.25, 0.2), 1.0 - smoothstep(-aa, aa, box(p - vec2(0.0, 0.66), vec2(0.5, 0.08), 0.01)));
    } else if (vShape == 20) {
      // Home: a house, its munitions a gauge filling the walls.
      float roof = max((abs(p.x) * 0.9 - 0.52 - p.y) * 0.74, p.y + 0.06);
      float walls = box(p - vec2(0.0, 0.24), vec2(0.42, 0.3), 0.02);
      float house = min(roof, walls);
      c = mix(c, team, 1.0 - smoothstep(-aa, aa, house));
      float level = mix(0.48, 0.0, vHp);
      float stock = (1.0 - smoothstep(-aa, aa, walls + 0.08)) * step(level, p.y);
      c = mix(c, vec3(0.07, 0.08, 0.1), (1.0 - smoothstep(-aa, aa, walls + 0.08)) * 0.85);
      c = mix(c, vec3(0.92, 0.86, 0.62), stock * 0.9);
    } else {
      // Depot: a crate, its stock a gauge filling from the bottom.
      float crate = box(p, vec2(0.5, 0.42), 0.04);
      float edge = 1.0 - smoothstep(-aa, aa, abs(crate) - 0.06);
      float level = mix(0.42, -0.42, vHp);
      float stock = (1.0 - smoothstep(-aa, aa, crate + 0.06)) * step(level, p.y);
      c = mix(c, vec3(0.92, 0.86, 0.62), stock * 0.9);
      c = mix(c, team, edge);
      // A field depot: an orange corner.
      if ((vFlags & 128) != 0) c = mix(c, vec3(1.0, 0.55, 0.12), 1.0 - smoothstep(-aa, aa, length(p - vec2(0.62, -0.62)) - 0.16));
    }
    c = mix(c, team, border);
    // A dark rim outside, for contrast with the ground.
    float halo = (1.0 - smoothstep(-aa, aa, plate - 0.12)) * (1.0 - a);
    outColor = vec4(mix(vec3(0.0), c, a), max(a, halo * 0.7) * vColor.a);
    if (outColor.a < 0.01) discard;
    return;
  } else if (vShape == 18 || vShape == 19) {
    // Industry: a hex outline round a glyph (a pit for mines, a works with a
    // chimney for plants); the glyph's fill is how built the site is.
    float ring = abs(hexDist(p) - 0.82) - 0.06;
    float glyph = vShape == 18
      ? tri(vec2(p.x, -p.y + 0.08), 0.48)
      : min(box(p + vec2(0.1, -0.12), vec2(0.42, 0.28), 0.04), box(p + vec2(-0.3, 0.2), vec2(0.1, 0.32), 0.02));
    float a = 1.0 - smoothstep(-aa, aa, ring);
    float g = 1.0 - smoothstep(-aa, aa, glyph);
    float outline = 1.0 - smoothstep(-aa, aa, abs(glyph) - 0.05);
    vec4 fill = vec4(vColor.rgb, g * (0.25 + 0.75 * vHp));
    outColor = mix(fill, vec4(vColor.rgb, 1.0), max(a, outline * 0.9));
    outColor.a = max(fill.a, max(a, outline * 0.9)) * vColor.a;
    if (outColor.a < 0.01) discard;
    return;
  } else if (vShape == 12) {
    d = box(p, vec2(0.95, 0.42), 0.12);
    float a = 1.0 - smoothstep(-aa, aa, d);
    float stripe = 1.0 - smoothstep(-aa, aa, box(p, vec2(0.75, 0.16), 0.05));
    vec3 body = mix(vec3(0.9), vColor.rgb, stripe);
    float edge = smoothstep(-aa * 3.0, 0.0, d);
    outColor = vec4(mix(body, vec3(0.1), edge * 0.8), a);
    if (a < 0.01) discard;
    return;
  } else if (vShape == 13) {
    float r = length(p);
    float a = exp(-r * r * 6.0);
    outColor = vec4(mix(vColor.rgb, vec3(1.0), a), a * vColor.a);
    if (outColor.a < 0.01) discard;
    return;
  } else if (vShape == 14) {
    float t = vAux;
    float ring = abs(length(p) - (0.3 + 0.6 * t)) - 0.12 * (1.0 - t);
    float a = (1.0 - smoothstep(-aa, aa, ring)) * (1.0 - t);
    float core = exp(-dot(p, p) * 8.0) * (1.0 - t) * (1.0 - t);
    outColor = vec4(mix(vColor.rgb, vec3(1.0, 0.95, 0.8), core), max(a, core) * vColor.a);
    if (outColor.a < 0.01) discard;
    return;
  } else if (vShape == 15) {
    vec2 q = abs(p);
    float x = min(abs(q.x - q.y), 10.0) - 0.14;
    x = max(x, max(q.x, q.y) - 0.6);
    float a = (1.0 - smoothstep(-aa, aa, x)) * (1.0 - vAux);
    outColor = vec4(vColor.rgb, a * vColor.a);
    if (outColor.a < 0.01) discard;
    return;
  } else if (vShape == 16 || vShape == 17) {
    float w = vShape == 17 ? 0.09 : 0.05;
    float ring = abs(length(p) - 0.9) - w;
    float a = 1.0 - smoothstep(-aa, aa, ring);
    if (vShape == 17) a *= 0.75 + 0.25 * sin(uTime * 6.0);
    outColor = vec4(vColor.rgb, a * vColor.a);
    if (outColor.a < 0.01) discard;
    return;
  } else discard;

  // A vehicle's gun: a barrel forward from the turret, past the hull.
  float barrel = (vParts & 2) != 0 ? box(p - vec2(0.62, 0.0), vec2(0.5, 0.1), 0.02) : 1e9;
  if ((vParts & 2) != 0) d = min(d, barrel);
  float inside = 1.0 - smoothstep(-aa, aa, d);
  // A dark outline, so units read on any ground and against each other.
  float outline = (1.0 - smoothstep(-aa, aa, d - 0.16)) * (1.0 - inside);
  if (inside < 0.01 && outline < 0.01 && (vFlags & 4) == 0) discard;
  // Units carry their side (aux = side + 1) for the rim, so any colouring
  // keeps sides readable.
  int side = int(vAux * 255.0 + 0.5) - 1;
  vec3 sideColor = side >= 0 ? texelFetch(uPalette, ivec2(side, 0), 0).rgb : col.rgb;
  vec3 fill = col.rgb;
  if ((vFlags & 32) != 0) fill *= 0.7;
  // Health: the fill drains from the top.
  float level = mix(-0.9, 0.9, vHp);
  if (p.y < -level + 0.0 && vHp < 0.999) fill *= 0.35;
  // Rim: the unit's side, so any colouring keeps sides readable.
  float rim = smoothstep(-aa * 2.5 - 0.12, -0.12 + aa, d);
  vec3 rimColor = side >= 0 ? sideColor : col.rgb * 0.35;
  vec3 c = mix(fill, rimColor, rim);
  // Dug in: an earthen outline.
  if ((vFlags & 8) != 0) c = mix(c, vec3(0.55, 0.42, 0.25), smoothstep(-0.2, -0.05, d) * 0.85);
  // Suppressed: a pulsing white core.
  if ((vFlags & 2) != 0) {
    float pulse = 0.5 + 0.5 * sin(uTime * 9.0);
    c = mix(c, vec3(1.0), (1.0 - smoothstep(0.0, 0.3, length(p))) * (0.35 + 0.4 * pulse));
  }
  // Knocked out (engine or gun): a dark cross over the unit.
  if (unit && (vFlags & 128) != 0) {
    float x = min(abs(p.x - p.y), abs(p.x + p.y)) * 0.7071 - 0.1;
    c = mix(c, vec3(0.06), (1.0 - smoothstep(-aa, aa, x)) * step(d, 0.0));
  }
  // Module marks, in a dark ink so they read on any team colour.
  vec3 ink = vec3(0.06, 0.06, 0.06);
  float inBody = step(d, 0.0);
  // Tracks: dark bands along a tracked hull's sides.
  if (vShape == 1) c = mix(c, ink, (1.0 - smoothstep(-aa, aa, abs(abs(p.y) - 0.56) - 0.1)) * inBody * 0.85);
  // Anti-armour weapon on foot: a bar across.
  if ((vParts & 1) != 0 || vShape == 5) c = mix(c, ink, 1.0 - smoothstep(-aa, aa, box(p, vec2(0.62, 0.13), 0.03)));
  // Armour: a plate, a second outline inside.
  if ((vParts & 8) != 0) c = mix(c, ink, (1.0 - smoothstep(-aa, aa, abs(d + 0.24) - 0.05)) * inBody);
  // Indirect fire: a chevron.
  if ((vParts & 4) != 0) {
    vec2 q = p - vec2(0.2, 0.12);
    float chev = abs(abs(q.x) * 0.9 + q.y - 0.05) - 0.08;
    chev = max(chev, abs(q.x) - 0.4);
    c = mix(c, ink, 1.0 - smoothstep(-aa, aa, chev));
  }
  // Cargo: a box, filled when loaded.
  if ((vParts & 16) != 0) {
    float bx = box(p, vec2(0.38, 0.2), 0.03);
    float mark = (vFlags & 64) != 0 ? 1.0 - smoothstep(-aa, aa, bx) : 1.0 - smoothstep(-aa, aa, abs(bx) - 0.04);
    c = mix(c, (vFlags & 64) != 0 ? vec3(0.95, 0.9, 0.7) : ink, mark);
  }
  // No weapon (prisoners): hollow.
  if ((vParts & 32) != 0) c = mix(c, vec3(0.12), (1.0 - smoothstep(-aa, aa, d + 0.3)));
  // Engineers: a wrench, its jaw open to the upper right.
  if ((vParts & 64) != 0) {
    float handle = seg(p, vec2(-0.42, 0.42), vec2(0.1, -0.1)) - 0.09;
    vec2 j = p - vec2(0.22, -0.22);
    float jaw = max(abs(length(j) - 0.2) - 0.08, -box(j - vec2(0.12, -0.12), vec2(0.1), 0.0));
    c = mix(c, ink, 1.0 - smoothstep(-aa, aa, min(handle, jaw)));
  }
  // Prospectors: a survey tripod.
  if ((vParts & 128) != 0) {
    vec2 top = vec2(0.0, -0.42);
    float legs = min(min(seg(p, top, vec2(-0.44, 0.44)), seg(p, top, vec2(0.0, 0.5))), seg(p, top, vec2(0.44, 0.44))) - 0.065;
    float head = length(p - top) - 0.14;
    c = mix(c, ink, 1.0 - smoothstep(-aa, aa, min(legs, head)));
  }
  c = mix(vec3(0.03, 0.035, 0.04), c, inside);
  float alpha = max(inside, outline * 0.8);
  if ((vFlags & 4) != 0) {
    float sel = 1.0 - smoothstep(-aa, aa, abs(d - 0.22) - 0.07);
    c = mix(c, vec3(1.0), sel * (1.0 - inside));
    alpha = max(alpha, sel);
  }
  outColor = vec4(c, alpha * col.a);
}`;

/** Batches instanced sprites (units, markers, trains, shells, effects) into one draw. */
export class Sprites {
  private program: Program;
  private vao: WebGLVertexArrayObject;
  private floatBuf: WebGLBuffer;
  private byteBuf: WebGLBuffer;
  /** Side colours, a texel per side (256: a side is a byte). */
  private palette = new Uint8Array(256 * 4);
  private paletteTex: WebGLTexture | null = null;
  private paletteDirty = true;
  private floats = new Float32Array(FLOATS * 4096);
  private bytes = new Uint8Array(BYTES * 4096);
  count = 0;

  constructor(private gl: GL) {
    this.program = new Program(gl, VERTEX, FRAGMENT);
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const quad = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.floatBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.floatBuf);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, FLOATS * 4, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, FLOATS * 4, 16);
    gl.vertexAttribDivisor(2, 1);
    this.byteBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.byteBuf);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.UNSIGNED_BYTE, true, BYTES, 0);
    gl.vertexAttribDivisor(3, 1);
    gl.enableVertexAttribArray(4);
    gl.vertexAttribPointer(4, 4, gl.UNSIGNED_BYTE, true, BYTES, 4);
    gl.vertexAttribDivisor(4, 1);
    gl.bindVertexArray(null);
  }

  /** Side colours, for unit rims. */
  setFactions(colors: [number, number, number][]): void {
    const next = new Uint8Array(256 * 4);
    colors.slice(0, 256).forEach((c, i) => next.set([c[0] * 255, c[1] * 255, c[2] * 255, 255], i * 4));
    if (next.some((v, i) => v !== this.palette[i])) {
      this.palette = next;
      this.paletteDirty = true;
    }
  }

  begin(): void {
    this.count = 0;
  }

  push(
    x: number,
    y: number,
    radius: number,
    shape: Shape,
    color: readonly [number, number, number],
    alpha = 1,
    hp = 1,
    flags = 0,
    aux = 0,
    angle = 0,
    parts = 0,
  ): void {
    if (this.count * FLOATS >= this.floats.length) {
      const f = new Float32Array(this.floats.length * 2);
      f.set(this.floats);
      this.floats = f;
      const b = new Uint8Array(this.bytes.length * 2);
      b.set(this.bytes);
      this.bytes = b;
    }
    const i = this.count++;
    const f = this.floats;
    f[i * FLOATS] = x;
    f[i * FLOATS + 1] = y;
    f[i * FLOATS + 2] = radius;
    f[i * FLOATS + 3] = shape;
    f[i * FLOATS + 4] = angle;
    const b = this.bytes;
    b[i * BYTES] = color[0] * 255;
    b[i * BYTES + 1] = color[1] * 255;
    b[i * BYTES + 2] = color[2] * 255;
    b[i * BYTES + 3] = alpha * 255;
    b[i * BYTES + 4] = hp * 255;
    b[i * BYTES + 5] = flags;
    b[i * BYTES + 6] = aux * 255;
    b[i * BYTES + 7] = parts;
  }

  draw(center: [number, number], zoom: number, viewport: [number, number], time: number, minPx: number): void {
    if (this.count === 0) return;
    const gl = this.gl;
    const p = this.program;
    p.use();
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.floatBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.floats.subarray(0, this.count * FLOATS), gl.STREAM_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.byteBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.bytes.subarray(0, this.count * BYTES), gl.STREAM_DRAW);
    gl.uniform2f(p.u("uCenter"), center[0], center[1]);
    gl.uniform1f(p.u("uZoom"), zoom);
    gl.uniform2f(p.u("uViewport"), viewport[0], viewport[1]);
    gl.uniform1f(p.u("uMinPx"), minPx);
    gl.uniform1f(p.u("uTime"), time);
    if (!this.paletteTex) {
      this.paletteTex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, this.paletteTex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }
    gl.activeTexture(gl.TEXTURE12);
    gl.bindTexture(gl.TEXTURE_2D, this.paletteTex);
    if (this.paletteDirty) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, this.palette);
      this.paletteDirty = false;
    }
    gl.uniform1i(p.u("uPalette"), 12);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.count);
    gl.bindVertexArray(null);
  }
}
